/**
 * Failed-persona counts a finished result reports (spec 76-06, contract C2). Older results carry
 * none of these fields and yield no notice.
 */

const SURVEY_ANSWERS_SUFFIX = 'Each question shows how many it is missing.';
const NON_SURVEY_COUNT_KEYS = ['failed_personas', 'failed_reactions'] as const;
const FULL_STOP = '.';

export interface FailedCountNotice {
  banner: string;
  /** The note the backend also prefixed to the result text, if any. */
  note: string | null;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isPositiveCount = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0;

/** "8 of 10 respondents" when some personas never answered; older results keep the plain count. */
export function respondentsLabel(results: {
  total_respondents: number;
  answered_respondents?: number;
}): string {
  const { total_respondents: total, answered_respondents: answered } = results;
  return typeof answered === 'number' && answered < total
    ? `${answered} of ${total} respondents`
    : `${total} respondents`;
}

export function surveyFailedAnswersBanner(failed: number): string {
  return failed === 1
    ? `1 answer could not be simulated and is left out of these results. ${SURVEY_ANSWERS_SUFFIX}`
    : `${failed} answers could not be simulated and are left out of these results. ${SURVEY_ANSWERS_SUFFIX}`;
}

function parseJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}

export function failedCountNotice(response: string): FailedCountNotice | null {
  const parsed = parseJson(response);
  if (!isRecord(parsed)) return null;

  if (isRecord(parsed.survey_results)) {
    const failed = parsed.survey_results.failed_responses_total;
    return isPositiveCount(failed)
      ? { banner: surveyFailedAnswersBanner(failed), note: null }
      : null;
  }

  const note = parsed.data_quality_note;
  const hasFailures = NON_SURVEY_COUNT_KEYS.some((key) => isPositiveCount(parsed[key]));
  if (!hasFailures || typeof note !== 'string' || !note) return null;
  return { banner: note.endsWith(FULL_STOP) ? note : `${note}${FULL_STOP}`, note };
}

/** Drops exactly the `_<note>._` line (and its blank line) the backend put ahead of the text. */
export function stripDataQualityNote(text: string, note: string | null): string {
  if (!note || typeof text !== 'string') return text;
  const prefix = `_${note}._\n\n`;
  return text.startsWith(prefix) ? text.slice(prefix.length) : text;
}
